import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type SlackInfo,
} from '../types';

const mapSlackInfos = (data: unknown, workspaceAlias: string): SlackInfo[] => {
  if (!Array.isArray(data)) return [];

  const mapped = data
    .map((item) => {
      if (!item || typeof item !== 'object') return null;

      const info = item as {
        team_id?: string;
        team_name?: string;
      };

      if (!info.team_id || !info.team_name) return null;

      return {
        id: info.team_id,
        workspace_id: workspaceAlias,
        alias: info.team_id,
        import_state: IMPORT_STATE.NOT_STARTED,
        history: [] as ImportHistoryItemT[],
        name: info.team_name,
        channels: [],
      };
    })
    .filter(Boolean);

  return mapped as SlackInfo[];
};

export const useSlack = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(SOURCE_TYPE.SLACK, workspaceAlias);

  const { data: slackWorkspaces = [] } = useQuery({
    queryKey,
    queryFn: async () =>
      mapSlackInfos(
        await service.workspace.fetchWorkspaceSlackInfos(workspaceAlias),
        workspaceAlias,
      ),
    enabled,
  });

  const { mutate: disconnectSlackMutation } = useMutation({
    mutationFn: (teamId: string) =>
      service.slack.deleteSlackInfo(workspaceAlias, teamId),
    onSuccess: (_, teamId) => {
      queryClient.setQueryData<SlackInfo[]>(queryKey, (current = []) =>
        current.filter((item) => item.id !== teamId),
      );
    },
  });

  return {
    slackWorkspaces,
    disconnectSlackMutation,
    invalidateSlackQuery: () => queryClient.invalidateQueries({ queryKey }),
  };
};
